import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { api } from '../../../convex/_generated/api';
import { isValidProjectId } from '../../utils/projectIds';
import { compareProjectFilesByOrder } from '../../../shared/projectFileOrder';
import {
  CHALLENGE_PROJECT_DRAFT_PATH,
  CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH,
  CHALLENGE_PROJECT_SUBMISSION_PATH,
} from '../../../shared/challengeProjectSeed';
import './ReaderPreviewPage.css';

const DEFAULT_SETTINGS = Object.freeze({
  pagePreset: 'us-letter',
  fontSizePx: 12,
  lineHeight: 1.65,
  headingColor: '#111111',
  bodyColor: '#1b1b1b',
  pageColor: '#f8f5ef',
  headingFontKey: 'garamond',
  bodyFontKey: 'georgia',
  headingScale: 1,
  paragraphSpacing: 1,
  pagePaddingPx: 56,
});

const PAGE_PRESETS = {
  'us-letter': {
    labelKey: 'readerPreview.pagePresets.usLetter',
    printSize: '8.5in 11in',
    screenWidth: '760px',
  },
  'trade-6x9': {
    labelKey: 'readerPreview.pagePresets.trade6x9',
    printSize: '6in 9in',
    screenWidth: '620px',
  },
};

const FONT_OPTIONS = [
  {
    key: 'garamond',
    label: 'Garamond',
    family: 'Garamond, Baskerville, "Times New Roman", serif',
  },
  {
    key: 'georgia',
    label: 'Georgia',
    family: 'Georgia, Cambria, "Times New Roman", serif',
  },
  {
    key: 'palatino',
    label: 'Palatino',
    family: 'Palatino, "Palatino Linotype", "Book Antiqua", serif',
  },
  {
    key: 'times',
    label: 'Times',
    family: '"Times New Roman", Times, serif',
  },
  {
    key: 'system-serif',
    label: 'System Serif',
    family: 'ui-serif, Georgia, serif',
  },
];

const FONT_BY_KEY = Object.fromEntries(FONT_OPTIONS.map((font) => [font.key, font.family]));
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const isMarkdownPath = (path) => {
  const lower = path.toLowerCase();
  return lower.endsWith('.md') || lower.endsWith('.markdown');
};

const sortSiblingEntries = (left, right) => {
  if (left.type !== right.type) {
    return left.type === 'folder' ? -1 : 1;
  }

  const orderLeft = typeof left.order === 'number' ? left.order : null;
  const orderRight = typeof right.order === 'number' ? right.order : null;

  if (orderLeft !== null && orderRight !== null && orderLeft !== orderRight) {
    return orderLeft - orderRight;
  }
  if (orderLeft !== null && orderRight === null) return -1;
  if (orderLeft === null && orderRight !== null) return 1;

  return left.name.localeCompare(right.name);
};

const buildFileTreeOrder = (files) => {
  const groups = new Map();

  for (const file of files) {
    const parent = file.parentPath || '';
    const siblings = groups.get(parent) || [];
    siblings.push(file);
    groups.set(parent, siblings);
  }

  const orderedPaths = [];

  const walk = (parentPath) => {
    const siblings = (groups.get(parentPath) || []).slice().sort(sortSiblingEntries);
    for (const entry of siblings) {
      if (entry.type === 'file') {
        orderedPaths.push(entry.path);
      } else {
        walk(entry.path);
      }
    }
  };

  walk('');
  return orderedPaths;
};

const dedupePaths = (paths) => {
  const seen = new Set();
  const result = [];

  for (const path of paths) {
    if (seen.has(path)) continue;
    seen.add(path);
    result.push(path);
  }

  return result;
};

const normalizeFontKey = (key, fallback) => {
  if (typeof key === 'string' && FONT_BY_KEY[key]) {
    return key;
  }
  return fallback;
};

const normalizeColor = (value, fallback) => {
  if (typeof value === 'string' && HEX_COLOR_RE.test(value)) {
    return value.toLowerCase();
  }
  return fallback;
};

const normalizeSettings = (settings) => {
  const pagePreset = settings?.pagePreset === 'trade-6x9' ? 'trade-6x9' : 'us-letter';

  const rawFontSize = Number(settings?.fontSizePx);
  const rawLineHeight = Number(settings?.lineHeight);
  const rawHeadingScale = Number(settings?.headingScale);
  const rawParagraphSpacing = Number(settings?.paragraphSpacing);
  const rawPagePadding = Number(settings?.pagePaddingPx);

  const fontSizePx = Number.isFinite(rawFontSize)
    ? Math.round(clamp(rawFontSize, 10, 20))
    : DEFAULT_SETTINGS.fontSizePx;
  const lineHeight = Number.isFinite(rawLineHeight)
    ? Math.round(clamp(rawLineHeight, 1.2, 2.2) * 100) / 100
    : DEFAULT_SETTINGS.lineHeight;

  const headingScale = Number.isFinite(rawHeadingScale)
    ? Math.round(clamp(rawHeadingScale, 0.8, 1.45) * 100) / 100
    : DEFAULT_SETTINGS.headingScale;
  const paragraphSpacing = Number.isFinite(rawParagraphSpacing)
    ? Math.round(clamp(rawParagraphSpacing, 0.8, 1.8) * 100) / 100
    : DEFAULT_SETTINGS.paragraphSpacing;
  const pagePaddingPx = Number.isFinite(rawPagePadding)
    ? Math.round(clamp(rawPagePadding, 32, 90))
    : DEFAULT_SETTINGS.pagePaddingPx;

  return {
    pagePreset,
    fontSizePx,
    lineHeight,
    headingColor: normalizeColor(settings?.headingColor, DEFAULT_SETTINGS.headingColor),
    bodyColor: normalizeColor(settings?.bodyColor, DEFAULT_SETTINGS.bodyColor),
    pageColor: normalizeColor(settings?.pageColor, DEFAULT_SETTINGS.pageColor),
    headingFontKey: normalizeFontKey(settings?.headingFontKey, DEFAULT_SETTINGS.headingFontKey),
    bodyFontKey: normalizeFontKey(settings?.bodyFontKey, DEFAULT_SETTINGS.bodyFontKey),
    headingScale,
    paragraphSpacing,
    pagePaddingPx,
  };
};

const toSafeMarkdownHtml = (content) => {
  const parsed = marked.parse(content || '');
  const html = typeof parsed === 'string' ? parsed : '';
  return DOMPurify.sanitize(html);
};

const configSignature = (selectedPaths, settings) =>
  JSON.stringify({
    selectedPaths,
    settings: normalizeSettings(settings),
  });

function SortableSelectedItem({ file, onRemove }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: file.path,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li ref={setNodeRef} style={style} className={`rp-selected-item ${isDragging ? 'dragging' : ''}`}>
      <button
        type="button"
        className="rp-drag-handle"
        aria-label={t('common.reorderItem', { path: file.path })}
        {...attributes}
        {...listeners}
      >
        ⋮⋮
      </button>
      <div className="rp-selected-copy">
        <span className="rp-selected-name">{file.name}</span>
        <span className="rp-selected-path">{file.path}</span>
      </div>
      <button type="button" className="rp-item-action" onClick={() => onRemove(file.path)}>{t("common.remove")}</button>
    </li>
  );
}

function ReaderPreviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const safeProjectId = isValidProjectId(projectId) ? projectId : null;

  const project = useQuery(api.projects.getById, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const files = useQuery(api.files.listByProject, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const config = useQuery(api.readerPreview.getConfig, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const saveConfig = useMutation(api.readerPreview.saveConfig);

  const [activeTab, setActiveTab] = useState('layout');
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [saveState, setSaveState] = useState('idle');
  const [saveError, setSaveError] = useState('');
  const [reconciledRemovedPaths, setReconciledRemovedPaths] = useState([]);
  const [activeDragPath, setActiveDragPath] = useState(null);

  const lastSavedSignatureRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const markdownFiles = useMemo(() => {
    if (!files) return [];
    return files.filter((file) => file.type === 'file' && isMarkdownPath(file.path));
  }, [files]);

  const orderedFilePaths = useMemo(() => {
    if (!files) return [];
    return buildFileTreeOrder(files);
  }, [files]);

  const pathOrderIndex = useMemo(
    () => new Map(orderedFilePaths.map((path, index) => [path, index])),
    [orderedFilePaths],
  );

  const markdownPathSet = useMemo(
    () => new Set(markdownFiles.map((file) => file.path)),
    [markdownFiles],
  );

  const markdownFileMap = useMemo(
    () => new Map(markdownFiles.map((file) => [file.path, file])),
    [markdownFiles],
  );
  const hasCanonicalChallengeSubmission = markdownPathSet.has(CHALLENGE_PROJECT_SUBMISSION_PATH);
  const isDefaultPreviewPath = (path) =>
    (path.startsWith('Chapters/') && path !== CHALLENGE_PROJECT_DRAFT_PATH) ||
    (!hasCanonicalChallengeSubmission && path === CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH);

  useEffect(() => {
    if (hasInitialized || !files || config === undefined) return;

    const chapterDefaults = orderedFilePaths.filter(
      (path) => isDefaultPreviewPath(path) && markdownPathSet.has(path),
    );

    let nextSelectedPaths = chapterDefaults;
    let nextSettings = DEFAULT_SETTINGS;
    let removed = [];
    let previousSignature = null;

    if (config) {
      const seen = new Set();
      const kept = [];
      for (const path of config.selectedPaths) {
        if (seen.has(path)) continue;
        seen.add(path);

        if (markdownPathSet.has(path)) {
          kept.push(path);
        } else {
          removed.push(path);
        }
      }

      nextSelectedPaths = kept;
      nextSettings = normalizeSettings(config.settings);
      previousSignature = configSignature(config.selectedPaths, config.settings);
    }

    setSelectedPaths(nextSelectedPaths);
    setSettings(nextSettings);
    setReconciledRemovedPaths(removed);
    lastSavedSignatureRef.current = previousSignature;
    setHasInitialized(true);
  }, [config, files, hasCanonicalChallengeSubmission, hasInitialized, markdownPathSet, orderedFilePaths]);

  useEffect(() => {
    if (!hasInitialized) return;

    const missing = selectedPaths.filter((path) => !markdownPathSet.has(path));
    if (missing.length === 0) return;

    setSelectedPaths((current) => current.filter((path) => markdownPathSet.has(path)));
    setReconciledRemovedPaths((current) => dedupePaths([...current, ...missing]));
  }, [hasInitialized, markdownPathSet, selectedPaths]);

  useEffect(() => {
    if (!hasInitialized || !safeProjectId) return;

    const normalizedSettings = normalizeSettings(settings);
    const nextSignature = configSignature(selectedPaths, normalizedSettings);
    if (nextSignature === lastSavedSignatureRef.current) return;

    setSaveError('');
    setSaveState('saving');

    const timeout = setTimeout(async () => {
      try {
        const saved = await saveConfig({
          projectId: safeProjectId,
          selectedPaths,
          settings: normalizedSettings,
        });
        lastSavedSignatureRef.current = configSignature(saved.selectedPaths, saved.settings);
        setSaveState('saved');
      } catch (error) {
        setSaveState('error');
        setSaveError(error.message || 'Failed to save Reader Preview configuration.');
      }
    }, 550);

    return () => clearTimeout(timeout);
  }, [hasInitialized, safeProjectId, saveConfig, selectedPaths, settings]);

  useEffect(() => {
    if (saveState !== 'saved') return undefined;
    const timeout = setTimeout(() => setSaveState('idle'), 1400);
    return () => clearTimeout(timeout);
  }, [saveState]);

  const selectedPathSet = useMemo(() => new Set(selectedPaths), [selectedPaths]);

  const selectedFiles = useMemo(
    () => selectedPaths.map((path) => markdownFileMap.get(path)).filter(Boolean),
    [markdownFileMap, selectedPaths],
  );

  const availableFiles = useMemo(() => {
    return markdownFiles
      .filter((file) => !selectedPathSet.has(file.path))
      .sort((left, right) => {
        const leftOrder = pathOrderIndex.get(left.path) ?? Number.MAX_SAFE_INTEGER;
        const rightOrder = pathOrderIndex.get(right.path) ?? Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) return leftOrder - rightOrder;
        return compareProjectFilesByOrder(left, right);
      });
  }, [markdownFiles, pathOrderIndex, selectedPathSet]);

  const previewSections = useMemo(
    () =>
      selectedFiles.map((file) => ({
        path: file.path,
        html: toSafeMarkdownHtml(file.content || ''),
      })),
    [selectedFiles],
  );

  const emptySelectedFiles = useMemo(
    () => selectedFiles.filter((file) => !(file.content || '').trim()),
    [selectedFiles],
  );

  const canPrint = selectedFiles.length > 0 && emptySelectedFiles.length === 0;
  const activePreset = PAGE_PRESETS[settings.pagePreset] || PAGE_PRESETS['us-letter'];
  const printStyle = `@media print { @page { size: ${activePreset.printSize}; margin: 0.72in; } }`;

  const previewStyle = {
    '--rp-font-size': `${settings.fontSizePx}px`,
    '--rp-line-height': String(settings.lineHeight),
    '--rp-page-width': activePreset.screenWidth,
    '--rp-heading-color': settings.headingColor,
    '--rp-body-color': settings.bodyColor,
    '--rp-page-bg': settings.pageColor,
    '--rp-heading-font': FONT_BY_KEY[settings.headingFontKey] || FONT_BY_KEY[DEFAULT_SETTINGS.headingFontKey],
    '--rp-body-font': FONT_BY_KEY[settings.bodyFontKey] || FONT_BY_KEY[DEFAULT_SETTINGS.bodyFontKey],
    '--rp-heading-scale': String(settings.headingScale),
    '--rp-paragraph-spacing': String(settings.paragraphSpacing),
    '--rp-page-padding': `${settings.pagePaddingPx}px`,
  };

  const activeDragFile = activeDragPath ? markdownFileMap.get(activeDragPath) : null;
  const tabOptions = [
    { id: 'layout', label: t('readerPreview.tabs.layout') },
    { id: 'typography', label: t('readerPreview.tabs.typography') },
    { id: 'colors', label: t('readerPreview.tabs.colors') },
    { id: 'print', label: t('readerPreview.tabs.print') },
  ];

  const updateSettings = (patch) => {
    setSettings((current) => normalizeSettings({ ...current, ...patch }));
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  const handleAddPath = (path) => {
    setSelectedPaths((current) => (current.includes(path) ? current : [...current, path]));
  };

  const handleRemovePath = (path) => {
    setSelectedPaths((current) => current.filter((candidate) => candidate !== path));
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveDragPath(null);
    if (!over || active.id === over.id) return;

    setSelectedPaths((current) => {
      const oldIndex = current.indexOf(active.id);
      const newIndex = current.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return current;
      return arrayMove(current, oldIndex, newIndex);
    });
  };

  const handlePrint = () => {
    if (!canPrint) return;
    window.print();
  };

  if (!safeProjectId) {
    return (
      <main className="reader-preview loading">
        <div className="reader-preview-card">
          {t('common.invalidProjectLink')}
          <button className="btn btn-secondary" onClick={() => navigate('/projects')}>
            {t('common.backToProjects')}
          </button>
        </div>
      </main>
    );
  }

  if (project === undefined || files === undefined || config === undefined) {
    return (
      <main className="reader-preview loading">
        <div className="reader-preview-card">{t('readerPreview.loadingWorkspace')}</div>
      </main>
    );
  }

  if (!project) {
    return (
      <main className="reader-preview loading">
        <div className="reader-preview-card">{t('common.projectNotFound')}</div>
      </main>
    );
  }

    return (
      <main className="reader-preview" aria-label={t('readerPreview.workspaceAria', 'Reader Preview workspace')}>
      <style>{printStyle}</style>

      <header className="rp-header">
        <div>
          <span className="rp-tag">{t("readerPreview.tag")}</span>
          <h1>{project.title}</h1>
          <p>{t("readerPreview.subtitle")}</p>
        </div>
        <div className="rp-header-actions rp-shell-controls">
          <button className="btn btn-secondary" onClick={() => navigate(`/projects/${safeProjectId}`)}>{t("readerPreview.backToProject")}</button>
          <button className="btn btn-primary" onClick={handlePrint} disabled={!canPrint}>{t("readerPreview.printSavePdf")}</button>
        </div>
      </header>

      <section className="rp-status-banner rp-shell-controls" aria-live="polite">
        {saveState === 'saving' && <span>{t("readerPreview.savingLayout")}</span>}
        {saveState === 'saved' && <span>{t("readerPreview.layoutSaved")}</span>}
        {saveState === 'error' && <span className="error-text">{saveError}</span>}
        {saveState === 'idle' && <span>{t("readerPreview.layoutAutoSaves")}</span>}
      </section>

      {reconciledRemovedPaths.length > 0 ? (
        <section className="rp-missing-notice rp-shell-controls">
          {t('readerPreview.removedMissingFiles', { paths: reconciledRemovedPaths.join(', ') })}
        </section>
      ) : null}

      {emptySelectedFiles.length > 0 ? (
        <section className="rp-empty-warning rp-shell-controls" role="alert">
          {t('readerPreview.printBlocked', { paths: emptySelectedFiles.map((file) => file.path).join(', ') })}
        </section>
      ) : null}

      <section className="rp-grid">
        <section className="rp-preview-shell">
          <div className="rp-preview-header rp-shell-controls">
            <h2>{t("readerPreview.bookPreview")}</h2>
            <span>{t('readerPreview.sectionCount', { count: selectedFiles.length })}</span>
          </div>

          <div className="rp-book" style={previewStyle}>
            <section className="rp-book-page rp-title-page">
              <div className="rp-title-inner">
                <div className="rp-title-label">{t("readerPreview.readerPreviewTitle")}</div>
                <h3>{project.title}</h3>
                {project.synopsis ? (
                  <p>{project.synopsis}</p>
                ) : (
                  <p className="rp-title-muted">{t("readerPreview.noSynopsis")}</p>
                )}
              </div>
            </section>

            {previewSections.length === 0 ? (
              <section className="rp-book-page rp-content-page">
                <p className="rp-muted">{t("readerPreview.addMarkdownFiles")}</p>
              </section>
            ) : (
              previewSections.map((section) => (
                <section key={section.path} className="rp-book-page rp-content-page">
                  <div className="rp-page-path">{section.path}</div>
                  <article
                    className="rp-markdown-preview"
                    dangerouslySetInnerHTML={{ __html: section.html }}
                  />
                </section>
              ))
            )}
          </div>
        </section>

        <aside className="rp-controls rp-shell-controls">
          <article className="rp-card rp-controls-card">
            <div className="rp-card-header">
              <h2>{t("readerPreview.readerControls")}</h2>
              <button type="button" className="rp-reset-btn" onClick={resetSettings}>{t("readerPreview.resetDefaults")}</button>
            </div>

            <div className="rp-tabs" role="tablist" aria-label={t('readerPreview.controlsAria', 'Reader preview controls')}>
              {tabOptions.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`rp-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'layout' ? (
              <div className="rp-tab-panel">
                <label>{t("readerPreview.layout.pagePreset")}
                  <select
                    value={settings.pagePreset}
                    onChange={(event) => updateSettings({ pagePreset: event.target.value })}
                  >
                    {Object.entries(PAGE_PRESETS).map(([value, preset]) => (
                      <option key={value} value={value}>
                        {t(preset.labelKey)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>{t("readerPreview.layout.fontSize")}
                  <input
                    type="number"
                    min={10}
                    max={20}
                    step={1}
                    value={settings.fontSizePx}
                    onChange={(event) => updateSettings({ fontSizePx: Number(event.target.value) })}
                  />
                </label>
                <label>{t("readerPreview.layout.lineHeight")}
                  <input
                    type="number"
                    min={1.2}
                    max={2.2}
                    step={0.05}
                    value={settings.lineHeight}
                    onChange={(event) => updateSettings({ lineHeight: Number(event.target.value) })}
                  />
                </label>
                <label>{t("readerPreview.layout.pagePadding")}
                  <input
                    type="number"
                    min={32}
                    max={90}
                    step={2}
                    value={settings.pagePaddingPx}
                    onChange={(event) => updateSettings({ pagePaddingPx: Number(event.target.value) })}
                  />
                </label>
              </div>
            ) : null}

            {activeTab === 'typography' ? (
              <div className="rp-tab-panel">
                <label>{t("readerPreview.typography.headingFont")}
                  <select
                    value={settings.headingFontKey}
                    onChange={(event) => updateSettings({ headingFontKey: event.target.value })}
                  >
                    {FONT_OPTIONS.map((font) => (
                      <option key={font.key} value={font.key}>
                        {font.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>{t("readerPreview.typography.bodyFont")}
                  <select
                    value={settings.bodyFontKey}
                    onChange={(event) => updateSettings({ bodyFontKey: event.target.value })}
                  >
                    {FONT_OPTIONS.map((font) => (
                      <option key={font.key} value={font.key}>
                        {font.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>{t("readerPreview.typography.headingScale")}
                  <input
                    type="number"
                    min={0.8}
                    max={1.45}
                    step={0.05}
                    value={settings.headingScale}
                    onChange={(event) => updateSettings({ headingScale: Number(event.target.value) })}
                  />
                </label>
                <label>{t("readerPreview.typography.paragraphSpacing")}
                  <input
                    type="number"
                    min={0.8}
                    max={1.8}
                    step={0.05}
                    value={settings.paragraphSpacing}
                    onChange={(event) => updateSettings({ paragraphSpacing: Number(event.target.value) })}
                  />
                </label>
              </div>
            ) : null}

            {activeTab === 'colors' ? (
              <div className="rp-tab-panel">
                <label>{t("readerPreview.colors.headingColor")}
                  <div className="rp-color-row">
                    <input
                      type="color"
                      value={settings.headingColor}
                      onChange={(event) => updateSettings({ headingColor: event.target.value })}
                    />
                    <code>{settings.headingColor}</code>
                  </div>
                </label>
                <label>{t("readerPreview.colors.bodyColor")}
                  <div className="rp-color-row">
                    <input
                      type="color"
                      value={settings.bodyColor}
                      onChange={(event) => updateSettings({ bodyColor: event.target.value })}
                    />
                    <code>{settings.bodyColor}</code>
                  </div>
                </label>
                <label>{t("readerPreview.colors.pageColor")}
                  <div className="rp-color-row">
                    <input
                      type="color"
                      value={settings.pageColor}
                      onChange={(event) => updateSettings({ pageColor: event.target.value })}
                    />
                    <code>{settings.pageColor}</code>
                  </div>
                </label>
              </div>
            ) : null}

            {activeTab === 'print' ? (
              <div className="rp-tab-panel">
                <p className="rp-help-text">
                  {t('readerPreview.printTab.pageNumbers')} <strong>{t('readerPreview.printTab.headersAndFooters')}</strong>.
                </p>
                <p className="rp-help-text">
                  {t('readerPreview.printTab.pageBreak')}
                </p>
              </div>
            ) : null}
          </article>

          <article className="rp-card">
            <div className="rp-card-header">
              <h2>{t("readerPreview.selectedForPreview")}</h2>
              <span>{selectedFiles.length}</span>
            </div>
            {selectedFiles.length === 0 ? (
              <p className="rp-muted">{t("readerPreview.noFilesSelected")}</p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={(event) => setActiveDragPath(event.active.id)}
                onDragCancel={() => setActiveDragPath(null)}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={selectedFiles.map((file) => file.path)}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="rp-selected-list">
                    {selectedFiles.map((file) => (
                      <SortableSelectedItem key={file.path} file={file} onRemove={handleRemovePath} />
                    ))}
                  </ul>
                </SortableContext>
                <DragOverlay>
                  {activeDragFile ? (
                    <div className="rp-selected-item dragging overlay">
                      <span className="rp-selected-name">{activeDragFile.name}</span>
                      <span className="rp-selected-path">{activeDragFile.path}</span>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </article>

          <article className="rp-card">
            <div className="rp-card-header">
              <h2>{t("readerPreview.addMarkdownFilesTitle")}</h2>
              <span>{availableFiles.length}</span>
            </div>
            {availableFiles.length === 0 ? (
              <p className="rp-muted">{t("readerPreview.allFilesSelected")}</p>
            ) : (
              <ul className="rp-available-list">
                {availableFiles.map((file) => (
                  <li key={file.path}>
                    <div className="rp-available-copy">
                      <span>{file.name}</span>
                      <small>{file.path}</small>
                    </div>
                    <button type="button" className="rp-item-action" onClick={() => handleAddPath(file.path)}>{t("common.add")}</button>
                  </li>
                ))}
              </ul>
            )}
          </article>
        </aside>
      </section>
    </main>
  );
}

export default ReaderPreviewPage;
